import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export const FidemDictation = ({ onText, onBusy, disabled, prefix }) => {
  const { api } = useAuth();
  const [state, setState] = useState('idle');
  const active = useRef(true), recorder = useRef(null), stream = useRef(null), timer = useRef(null), request = useRef(null);
  const callbacks = useRef({ onText, onBusy }); callbacks.current = { onText, onBusy };
  const change = s => { if (active.current) { setState(s); callbacks.current.onBusy(s !== 'idle'); } };
  const stopTracks = () => { stream.current?.getTracks().forEach(t => t.stop()); clearTimeout(timer.current); };
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false; request.current?.abort();
      if (recorder.current) { recorder.current.onstop = null; if (recorder.current.state === 'recording') recorder.current.stop(); }
      stopTracks(); callbacks.current.onBusy(false);
    };
  }, []);
  const stop = () => { if (recorder.current?.state === 'recording') recorder.current.stop(); stopTracks(); };
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { toast.error('Este navegador no permite dictar. Puedes escribir tu consulta.'); return; }
    change('permission');
    try {
      const input = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!active.current) { input.getTracks().forEach(t => t.stop()); return; }
      stream.current = input;
      const mime = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find(t => MediaRecorder.isTypeSupported(t));
      if (!mime) throw new Error('Formato no disponible');
      const mr = new MediaRecorder(input, { mimeType: mime }); recorder.current = mr;
      let bytes = 0; const chunks = [];
      mr.ondataavailable = e => { if (e.data.size) { bytes += e.data.size; chunks.push(e.data); if (bytes >= 9 * 1024 * 1024) stop(); } };
      mr.onerror = () => { mr.onstop = null; stop(); change('idle'); toast.error('Se interrumpió la grabación.'); };
      mr.onstop = async () => {
        stopTracks(); if (!active.current) return; change('transcribing');
        try {
          if (!bytes) throw new Error('No se grabó audio');
          const form = new FormData();
          form.append('file', new Blob(chunks, { type: mime }), mime.includes('mp4') ? 'dictado.mp4' : 'dictado.webm');
          request.current = new AbortController();
          const { data } = await api.post('/voice-ai/transcribe', form, { signal: request.current.signal, timeout: 90000 });
          if (!active.current) return;
          if (data.text?.trim()) callbacks.current.onText(data.text.trim()); else toast.error('No se detectó voz.');
        } catch (e) { if (active.current && e.code !== 'ERR_CANCELED') toast.error('No se pudo transcribir el audio. Intenta nuevamente.'); }
        finally { change('idle'); }
      };
      mr.start(1000); change('recording'); timer.current = setTimeout(stop, 120000);
    } catch { stopTracks(); change('idle'); if (active.current) toast.error('No se pudo abrir el micrófono. Revisa el permiso del navegador.'); }
  };
  const label = state === 'recording' ? 'Detener dictado' : state === 'transcribing' ? 'Transcribiendo…' : state === 'permission' ? 'Esperando permiso…' : 'Dictar';
  return <button type="button" data-testid={`${prefix}-dictate`} aria-label={label} title="Dictado · OpenAI Whisper" aria-pressed={state === 'recording'} disabled={state !== 'recording' && (disabled || state !== 'idle')} onClick={state === 'recording' ? stop : start} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs transition-colors disabled:opacity-50 ${state === 'recording' ? 'border-rose-400 bg-rose-950 text-rose-200' : 'border-slate-700 text-slate-300 hover:border-amber-400'}`}>
    {state === 'recording' ? <Square size={14} /> : state === 'idle' ? <Mic size={14} /> : <Loader2 size={14} className="animate-spin" />}{label}
  </button>;
};